/**
 * The account button in the header and the sidebar.
 *
 * This replaces Clerk's <UserButton>. Clerk renders a photo whenever one
 * exists — and generates one when it does not — so the only way to show
 * initials is to own the trigger. The menu keeps what <UserButton> gave us:
 * Clerk's account modal and sign-out.
 */
import { useClerk, useUser } from "@clerk/clerk-react";
import { LogOut, UserRound } from "lucide-react";

import { cn } from "@/lib/utils";
import { initialsFrom } from "@/lib/initials";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export function UserMenu({ className }: { className?: string }) {
  const { user, isLoaded } = useUser();
  const { openUserProfile, signOut } = useClerk();

  const email = user?.primaryEmailAddress?.emailAddress ?? null;
  const initials = initialsFrom({
    firstName: user?.firstName,
    lastName: user?.lastName,
    fullName: user?.fullName,
    username: user?.username,
    email,
  });
  const name = user?.fullName ?? user?.username ?? email ?? "Account";

  // Clerk resolves the user a tick after the session, so the circle renders
  // empty rather than popping in from nothing and shifting the row.
  const label = isLoaded ? initials : "";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className={cn(
          "flex h-8 w-8 shrink-0 items-center justify-center rounded-full",
          "border border-primary/40 bg-primary/15 text-xs font-semibold text-primary",
          "transition-colors hover:bg-primary/25",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
          className,
        )}
        aria-label={`Account: ${name}`}
      >
        <span aria-hidden="true">{label}</span>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-56">
        <div className="px-2 py-1.5">
          <p className="truncate text-sm font-medium">{name}</p>
          {email && email !== name && (
            <p className="truncate text-xs text-muted-foreground">{email}</p>
          )}
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => openUserProfile()}>
          <UserRound />
          Manage account
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => void signOut()}>
          <LogOut />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
